import { useFormik } from 'formik';
import Head from 'next/head';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import * as Yup from 'yup';

import { Spacer } from '../../components/spacer';
import { Button } from '../../components/styled/button';
import {
  ErrorText,
  InputCheckbox,
  InputField,
  SelectField,
} from '../../components/styled/input';
import { Auth, AuthCard, Main } from '../../components/styled/layout';
import Logo from '../../components/styled/logo';
import { Heading, Text } from '../../components/styled/text';
import useMemberships from '../../data/use-memberships';
import { signup } from '../../endpoints/auth';
import format from '../../lib/util/currency';

const schema = Yup.object().shape({
  email: Yup.string().email('Ongeldig e-mailadres').required('Verplicht veld'),
  name: Yup.string().required('Veplicht veld'),
  surname: Yup.string().required('Veplicht veld'),
  job: Yup.string().required('Veplicht veld'),
  organisation: Yup.string().required('Veplicht veld'),
  tosAccepted: Yup.bool().oneOf([true], 'Verplicht veld'),
  membershipId: Yup.string()
    .uuid('Kies een lidmaatschap')
    .required('Verplicht veld'),
});

export default function Register() {
  const [signupError, setSignupError] = useState(null);
  const { data } = useMemberships();

  const router = useRouter();
  const formik = useFormik({
    validationSchema: schema,
    initialValues: {
      name: '',
      surname: '',
      job: '',
      email: '',
      organisation: '',
      tosAccepted: false,
      membershipId: 'placeholder',
    },
    onSubmit: async (values, form) => {
      setSignupError(null);
      try {
        await signup(values);
        router.push('/auth/login');
      } catch (err) {
        setSignupError(err.message);
        console.error(err);
      } finally {
        form.setSubmitting(false);
      }
    },
  });

  return (
    <>
      <Head>
        <title>Registreren</title>
      </Head>
      <Main>
        <Auth>
          <Spacer size={4} />
          <Logo />
          <Spacer size={4} />
          <form onSubmit={formik.handleSubmit}>
            <AuthCard>
              <Heading size="h1">Registreren</Heading>
              <Spacer size={1} />
              <SelectField
                disabled={!data}
                label="Lidmaatschap"
                name="membershipId"
                value={formik.values.membershipId}
                onChange={formik.handleChange}
                options={
                  <>
                    <option key="placeholder" value="placeholder" disabled>
                      Selecteer een lidmaatschap
                    </option>
                    {data?.memberships
                      .filter((membership) => membership.price !== 0)
                      .map((membership) => (
                        <option key={membership.id} value={membership.id}>
                          {membership.name} {format(membership.price)} / jaar
                        </option>
                      ))}
                  </>
                }
                helper={
                  <>
                    Heb je hulp nodig? Je kunt ons bereiken via{' '}
                    <a href="tel:00310880455500">088 – 04 555 00</a>.
                  </>
                }
                error={formik.errors.membershipId}
              />
              <Spacer size={0.5} />
              <InputField
                id="name"
                name="name"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.name}
                error={formik.errors.name}
                placeholder="Voornaam"
                label="Voornaam"
              />
              <Spacer size={0.5} />
              <InputField
                id="surname"
                name="surname"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.surname}
                error={formik.errors.surname}
                placeholder="Achternaam"
                label="Achternaam"
              />
              <Spacer size={0.5} />
              <InputField
                id="job"
                name="job"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.job}
                error={formik.errors.job}
                placeholder="Functie"
                label="Functie"
              />
              <Spacer size={0.5} />
              <InputField
                id="email"
                name="email"
                type="email"
                onChange={formik.handleChange}
                value={formik.values.email}
                error={formik.errors.email}
                placeholder="E-mailadres"
                label="E-mailadres"
              />
              <Spacer size={0.5} />
              <InputField
                id="organisation"
                name="organisation"
                type="text"
                onChange={formik.handleChange}
                value={formik.values.organisation}
                error={formik.errors.organisation}
                placeholder="Organisatie"
                label="Organisatie"
              />
              <Spacer size={1} />
              <InputCheckbox
                id="tosAccepted"
                name="tosAccepted"
                checked={formik.values.tosAccepted}
                error={formik.errors.tosAccepted}
                onChange={formik.handleChange}
                label={
                  <>
                    Ik accepteer de{' '}
                    <a
                      href="https://www.corporatienl.nl/algemene-voorwaarden/"
                      target="_blank"
                      rel="noreferrer"
                    >
                      algemene voorwaarden
                    </a>
                  </>
                }
              />
              {signupError ? (
                <>
                  <Spacer size={2} />
                  <ErrorText>{signupError}</ErrorText>
                </>
              ) : null}
              <Spacer size={2} />
              <Button
                type="submit"
                disabled={formik.isSubmitting || !formik.isValid}
              >
                Bevestigen en verzenden
              </Button>
              <Spacer size={1} />
              <Text>
                <Link href="/auth/login">
                  <a>Klik hier om in te loggen</a>
                </Link>
              </Text>
            </AuthCard>
          </form>
          <Spacer size={1} />
          <Text secondary>
            Heb je hulp nodig? Je kunt ons bereiken via{' '}
            <Link href="mailto:info@corporatienl.nl">
              <a>info@corporatienl.nl</a>
            </Link>
          </Text>
          <Spacer size={1} />
        </Auth>
      </Main>
    </>
  );
}
